<script lang="ts" setup>
import { computed, markRaw, onMounted, ref, watch } from 'vue';
import { useContestantStore, useEventCommonStoreRef, useEventStore, useGuiStore } from '@/stores';
import { Participant, RelayTeamContestant } from '@/objects/generated';
import { BaseObjListView } from '@/components';
import ContentComp from '@/components/layout/ContentComp.vue';
import { useHelper, useLoaders, useTaskLoader } from '@/composeables';
import { storeToRefs } from 'pinia';
import type { GridColumnOptions, IGridColumnCssRule } from '@/components/Grid/Classes';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import { propertyof } from '@/utilities';

import type BaseObjListViewCompExpose from '@/objects/BaseObjListViewCompExpose';
import { type IGridDetailExposed } from '@/components/Grid/Classes/IGridDetailExposed';
import ContestantRelayLegnumberRenderer from './ContestantRelayLegnumberRenderer.vue';
import RelayContestantFirstnameWithSearch from './RelayContestantFirstnameWithSearch.vue';
import { RelayTeamContestantDao } from '@/dao';

/**
 * The contestant id that we are showing the relay team for
 */
const contestantId = defineModel<number>({ required: true });

const { eventID } = useEventStore();

const props = withDefaults(
    defineProps<{
        /**
         * If this should be shown in a grid as a master detail view.
         * This will remove the toolbar and the status bar and shortcut keys.
         * This mean that saving etc needs to be handled by the parent component
         * @default false
         */
        gridDetailView?: boolean;
    }>(),
    {
        gridDetailView: false,
    },
);

const { AddLoader, LoadState, ClearLoaders } = useTaskLoader();
useLoaders(AddLoader, ['RELAYCONTESTANTS', 'STATIONS', 'STATIONROLES', 'RELAYCONTESTANTS']);

const list = ref<BaseObjListViewCompExpose<RelayTeamContestant> | null>(null);

/**
 * We use the contestant store for the contestants and the relay contestants
 */
const contestantStore = useContestantStore();
const { RelayContestants } = storeToRefs(contestantStore);

const { StationRoles } = useEventCommonStoreRef();

/**
 * The data of the grid
 */
const data = ref<RelayTeamContestant[]>([]);

/**
 * Callback for when the view get visible
 * @param state
 */
const isVisible = (isVisible: boolean) => {};

/**
 * Refresh the list
 */
const savedList = async (ids?: number[]) => {
    await refreshList(ids);
};

/**
 * Refresh the list
 */
const refreshList = async (ids?: number[]) => {
    ClearLoaders();
    AddLoader(async () => {
        // We force the source to reload the data
        await contestantStore.ReloadRelayContestants(ids);
    }, 'relaycontestants.loading');
};

/**
 * Callback when a new item is added to the grid.
 * We need to set the ContestantID of the current contestant
 * @param item
 */
const itemAdded = (item: RelayTeamContestant) => {
    item.ContestantID = contestantId.value;
};

/**
 * If we press CTRL+Enter we should add a new item to the grid
 * @param event
 */
const onCellKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
        list.value?.addItem();
        return useHelper().stopPropagation(event);
    }
};

/**
 * Will move the row up or down depending on the direction parameter
 * @param row
 * @param direction
 */
const moveRow: (row: RelayTeamContestant, direction: 'up' | 'down') => void = (row, direction) => {
    const index = data.value.findIndex((x) => x.ID === row.ID);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= data.value.length) return;

    // Swap the items
    const temp = data.value[newIndex];
    data.value[newIndex] = data.value[index];
    data.value[index] = temp;

    // We also need to swap the legnumber of the items
    const tempLegNumber = data.value[newIndex].LegNumber;
    data.value[newIndex].LegNumber = data.value[index].LegNumber;
    data.value[index].LegNumber = tempLegNumber;

    // We also need to swap the chip1 and chip2 of the items
    const tempChip1 = data.value[newIndex].Chip1;
    const tempChip2 = data.value[newIndex].Chip2;
    data.value[newIndex].Chip1 = data.value[index].Chip1;
    data.value[newIndex].Chip2 = data.value[index].Chip2;
    data.value[index].Chip1 = tempChip1;
    data.value[index].Chip2 = tempChip2;

    list.value?.dataUpdated();
};

/**
 * We add a custom column to the grid
 * @param columns
 */
const addCustomColumns = (columns: GridColumnOptions<RelayTeamContestant>[]) => {
    const legnumbercol = columns.find((x) => x.dataField == propertyof<RelayTeamContestant>('LegNumber'));
    if (legnumbercol) {
        legnumbercol.editable = false;
        legnumbercol.sortable = false;
        legnumbercol.columnHeaderText = '';
        legnumbercol.maxWidth = 50;
        legnumbercol.minWidth = 50;
        legnumbercol.resizable = false;
        legnumbercol.cellClass = 'border-0';

        legnumbercol.cellRenderer = markRaw(ContestantRelayLegnumberRenderer);
        legnumbercol.cellRendererParams = {
            rows: data.value,
            onButtonUpClicked: (row: RelayTeamContestant) => moveRow(row, 'up'),
            onButtonDownClicked: (row: RelayTeamContestant) => moveRow(row, 'down'),
        };
    }

    // We need the firstname column to replace the cellrenderer with our own that has the search functionality
    const firstnamecol = columns.find((x) => x.dataField == propertyof<RelayTeamContestant>('Firstname'));
    if (firstnamecol) {
        firstnamecol.cellRenderer = markRaw(RelayContestantFirstnameWithSearch);
        firstnamecol.cellRendererParams = {
            onParticipantSelected: (participant: Participant | null, row: RelayTeamContestant) => {
                if (participant) {
                    row.ParticipantID = participant.ID;
                    row.Firstname = participant.Firstname;
                    row.Email = participant.Email;
                    row.Birthday = participant.Birthday;
                    row.Gender = participant.Gender;
                    row.Surname = participant.Surname;
                } else {
                    row.ParticipantID = null;
                    row.Firstname = null;
                    row.Email = null;
                    row.Birthday = null;
                    row.Gender = null;
                    row.Surname = null;
                }
            },
        };
    }

    // Custom column for the leg name. We get it from the station roles
    let col = new GridColumnOptionsBuilder<RelayTeamContestant>(
        propertyof<RelayTeamContestant>('LegNumber'),
        'relayteamcontesteant.legname',
        'legname',
        'string',
    )
        .CssRule({
            className: 'fst-italic text-muted',
            func: () => true,
        })
        .ReadOnly()
        .Build();

    col.valueGetter = (data: RelayTeamContestant) => {
        if (!data.ContestantID) return '';

        let contestant = contestantStore.GetContestants([data.ContestantID!]);
        if (!contestant || contestant.length == 0 || contestant[0].RaceContestants?.length == 0) return '';

        let raceContestant = contestant[0].RaceContestants[0];

        // Get the
        let role = StationRoles.value.find((x) => x.RaceID == raceContestant.RaceID && x.RelayLegnumber == data.LegNumber);

        if (!role) {
            return '';
        }

        return role?.Name ?? '';
    };

    // Put the col in the third position of the columns array
    columns.splice(2, 0, col);

    columns.forEach((c) => {
        c.sortable = false;
        c.filterable = false;
    });

    return columns;
};

/**
 * Watch the relay contestants and update the data when the contestant id changes
 */
watch(
    () => RelayContestants.value,
    async () => {
        data.value = await RelayTeamContestantDao.GetRelayTeamContestantsByContestantID(eventID, contestantId.value);
        list.value?.dataUpdated();
    },
    { immediate: true },
);

onMounted(async () => {
    // data.value = await RelayTeamContestantDao.GetRelayTeamContestantsByContestantID(eventID, contestantId.value);
    // list.value?.dataUpdated();
});

/**
 * Helper property to check if the data in the grid has changed
 */
const dataHasChanged = ref(false);

/**
 * We expose some methods so we can use this as a component in a detail grid
 */
defineExpose<IGridDetailExposed<RelayTeamContestant>>({
    SaveGrid: () => {
        list.value?.saveList();
        return Promise.resolve();
    },

    IsChanged: computed(() => {
        return dataHasChanged.value;
    }),

    IsValid: computed(() => true),

    CanSave: computed(() => true),
});
</script>
<template>
    <ContentComp
        class="h-100 p-0"
        :fill-content="true"
        :show-as-modal="true"
        :style="{ height: '50vh !important' }"
        @is-visible="isVisible"
    >
        <BaseObjListView
            class="p-0 h-100"
            ref="list"
            v-model="data"
            :baseType="RelayTeamContestant"
            :custom-columns="addCustomColumns"
            :gui="useGuiStore().RelayTeamContestant"
            :hide-messages="props.gridDetailView"
            :hide-statusbar="true"
            :hide-toolbar="props.gridDetailView"
            :hideDefaultMenuActions="true"
            :hideSearchField="true"
            :list-loader="refreshList"
            :sort="(a: RelayTeamContestant, b: RelayTeamContestant) => (a.LegNumber ?? 0) - (b.LegNumber ?? 0)"
            :loadOnMount="false"
            :resize-strategy="'sizeColumnsToFit'"
            :use-action-header="props.gridDetailView"
            @cell-key-down="onCellKeyDown"
            @datachanged="(changed: boolean) => (dataHasChanged = changed)"
            @item-added="itemAdded"
            @saving-items-end="savedList"
        />
    </ContentComp>
</template>
